import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    CircularProgress,
    Alert,
    Chip,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import Axios from "axios";
import { getAuthHeader } from "../../utils/authToken";
import ConfirmDialog from "../../components/ConfirmDialog";
import ScheduleExamsDialog from "./ScheduleExamsDialog";
import AssignToField from "./AssignToField";
import { Field } from "./prepUi";
import { fmtNyDateTime, nyToday, nyDateTimeInput } from "./prepDates";

// Broad catalog seed(s) per program type — comma-separated, since ETT matches titles
// by substring and the half-length SAT series ("Half SAT n") shares no prefix with the
// full-length one. The server groups the merged titles into series; anything else lands
// under "Other". Empty → server's default seeds.
const CATALOG_SEED = { SAT: "Digital SAT,Half SAT", ACT: "ACT", AP: "AP" };

// Default add-form: release defaults to start-of-day, due to end-of-day (NY).
// The admin can edit the date and time of either; the server stores UTC.
const defaultExamForm = () => {
    const today = nyToday();
    return { release_date: `${today}T00:00`, due_date: `${today}T23:59`, order: "", assign_mode: "ALL", student_ids: [] };
};

// Compact chip label: just the number for the main series ("1"), "A1" for the A
// series, full title otherwise — the group header supplies the rest of the context.
const chipLabel = (groupKey, e) =>
    groupKey === "A_SERIES" ? `A${e.num}` : (e.num != null ? String(e.num) : e.title);

// Tests panel for prep_program_exams: an overview list of current exams, with an
// "Add exam" dialog that shows the ETT catalog as clickable chips (grouped by
// series) so the admin picks instead of typing. Persists immediately via the
// program's /exams endpoints; onChange lets the parent refresh its cached list.
const ProgramExamsPanel = ({ programUuid, programType, onChange }) => {
    const programsBase = `${process.env.REACT_APP_URL}/api/v1/prep-programs`;
    const base = `${programsBase}/${programUuid}`;
    const auth = { headers: { Authorization: getAuthHeader() } };

    const [exams, setExams] = useState([]);
    const [loading, setLoading] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [catalog, setCatalog] = useState([]); // [{ key, label, exams: [...] }]
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [catalogError, setCatalogError] = useState("");
    const [filter, setFilter] = useState("");
    const [selected, setSelected] = useState(null); // exam chosen from catalog, pending add
    const [form, setForm] = useState(defaultExamForm());
    const [editExam, setEditExam] = useState(null); // exam being edited (schedule + assignment)
    const [editForm, setEditForm] = useState(defaultExamForm());
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const confirmRef = useRef();

    const fetchExams = useCallback(async () => {
        setLoading(true);
        try {
            const res = await Axios.get(`${base}/exams`, auth);
            setExams(res.data);
        } catch (err) {
            console.error("Fetch program exams error:", err);
            setError("Failed to load exams");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programUuid]);

    useEffect(() => {
        fetchExams();
    }, [fetchExams]);

    const loadCatalog = async () => {
        setCatalogLoading(true);
        setCatalogError("");
        try {
            const res = await Axios.get(`${programsBase}/exam-catalog`, {
                ...auth,
                params: { q: CATALOG_SEED[programType] || "" },
            });
            setCatalog(res.data.groups || []);
        } catch (err) {
            console.error("Load exam catalog error:", err);
            setCatalogError("Failed to load the ETT exam catalog");
            setCatalog([]);
        } finally {
            setCatalogLoading(false);
        }
    };

    const openAddModal = () => {
        setFilter("");
        setSelected(null);
        setForm(defaultExamForm());
        setError("");
        setSuccess("");
        setAddOpen(true);
        loadCatalog();
    };

    const handleAdd = async () => {
        if (!selected) return;
        setError("");
        setSuccess("");
        try {
            await Axios.post(
                `${base}/exams`,
                {
                    ett_exam_id: selected.id,
                    exam_title: selected.title,
                    release_date: form.release_date || null,
                    due_date: form.due_date || null,
                    order: form.order === "" ? null : Number(form.order),
                    assign_mode: form.assign_mode || "ALL",
                    student_ids: form.assign_mode === "SELECTED" ? form.student_ids : [],
                },
                auth
            );
            setSuccess(`"${selected.title}" added`);
            setSelected(null);
            setForm(defaultExamForm());
            await fetchExams();
            if (onChange) onChange();
        } catch (err) {
            if (err.response?.status === 409) setError("This exam is already added to this program");
            else {
                console.error("Add exam error:", err);
                setError("Failed to add exam");
            }
        }
    };

    const doDelete = async (examId) => {
        try {
            await Axios.delete(`${base}/exams/${examId}`, auth);
            setExams((prev) => prev.filter((e) => e.id !== examId));
            if (onChange) onChange();
        } catch (err) {
            console.error("Delete exam error:", err);
            setError("Failed to remove exam");
        }
    };

    const confirmDelete = (exam) => {
        confirmRef.current.open({
            title: "Remove exam?",
            message: `Remove "${exam.exam_title}" from this program? This cannot be undone.`,
            confirmLabel: "Remove",
            onConfirm: () => doDelete(exam.id),
        });
    };

    const openEditExam = (exam) => {
        setError("");
        setEditForm({
            release_date: nyDateTimeInput(exam.release_date),
            due_date: nyDateTimeInput(exam.due_date),
            order: exam.order ?? "",
            description: exam.description || "",
            assign_mode: exam.assign_mode || "ALL",
            student_ids: exam.student_ids || [],
        });
        setEditExam(exam);
    };

    const saveEditExam = async () => {
        try {
            await Axios.put(
                `${base}/exams/${editExam.id}`,
                {
                    release_date: editForm.release_date || null,
                    due_date: editForm.due_date || null,
                    order: editForm.order === "" ? null : Number(editForm.order),
                    description: editForm.description || null,
                    assign_mode: editForm.assign_mode || "ALL",
                    student_ids: editForm.assign_mode === "SELECTED" ? editForm.student_ids : [],
                },
                auth
            );
            setEditExam(null);
            await fetchExams();
            if (onChange) onChange();
        } catch (err) {
            console.error("Update exam error:", err);
            setError("Failed to update exam");
        }
    };

    // Always list current exams in chronological order (by release date, then due, then
    // title); exams without a release date sort to the end.
    const sortedExams = [...exams].sort((a, b) => {
        const ra = a.release_date ? new Date(a.release_date).getTime() : Infinity;
        const rb = b.release_date ? new Date(b.release_date).getTime() : Infinity;
        const da = a.due_date ? new Date(a.due_date).getTime() : Infinity;
        const db = b.due_date ? new Date(b.due_date).getTime() : Infinity;
        return ra - rb || da - db || `${a.exam_title}`.localeCompare(`${b.exam_title}`);
    });

    const addedIds = new Set(exams.map((e) => e.ett_exam_id));
    const filterText = filter.trim().toLowerCase();
    const filteredGroups = catalog
        .map((g) => ({
            ...g,
            exams: filterText ? g.exams.filter((e) => e.title.toLowerCase().includes(filterText)) : g.exams,
        }))
        .filter((g) => g.exams.length);

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Full practice exams students sit in their practice course. The engine reads each
                student's results from these to measure weakness. Changes here save immediately.
            </Typography>

            {error && !addOpen && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Current exams</Typography>
            {loading ? (
                <Box display="flex" justifyContent="center" py={2}><CircularProgress size={24} /></Box>
            ) : exams.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No exams added yet.</Typography>
            ) : (
                <List dense sx={{ maxWidth: 640 }}>
                    {sortedExams.map((exam) => (
                        <ListItem key={exam.id} divider>
                            <ListItemText
                                primaryTypographyProps={{ component: "div" }}
                                primary={
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                        <span>{exam.exam_title}</span>
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            color={exam.assign_mode === "SELECTED" ? "warning" : "default"}
                                            label={exam.assign_mode === "SELECTED"
                                                ? `${(exam.student_ids || []).length} student${(exam.student_ids || []).length === 1 ? "" : "s"}`
                                                : "All students"}
                                        />
                                    </Box>
                                }
                                secondary={[
                                    exam.release_date && `release ${fmtNyDateTime(exam.release_date)}`,
                                    exam.due_date && `due ${fmtNyDateTime(exam.due_date)}`,
                                ].filter(Boolean).join(" · ")}
                            />
                            <ListItemSecondaryAction>
                                <IconButton size="small" onClick={() => openEditExam(exam)} aria-label="Edit exam">
                                    <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton edge="end" size="small" onClick={() => confirmDelete(exam)} aria-label="Remove exam">
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            )}

            <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={openAddModal}>
                    Add exam
                </Button>
                <Button variant="outlined" size="small" startIcon={<CalendarMonthIcon />} onClick={() => setScheduleOpen(true)}>
                    Add on a schedule
                </Button>
            </Box>

            {/* ---- Add exam dialog: catalog chips ---- */}
            <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add exam from ETT</DialogTitle>
                <DialogContent dividers>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
                    {catalogError && <Alert severity="error" sx={{ mb: 2 }}>{catalogError}</Alert>}

                    <Field label="Pick an exam">
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Filter by name…"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            sx={{ mb: 1.5 }}
                        />
                        {catalogLoading ? (
                            <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>
                        ) : filteredGroups.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">No exams found.</Typography>
                        ) : (
                            <Box sx={{ maxHeight: 300, overflow: "auto", pr: 0.5 }}>
                                <Box sx={{ display: "flex", gap: 2, mb: 1.5, alignItems: "center" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "info.main" }} />
                                        <Typography variant="caption" color="text.secondary">Adaptive</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: "50%", border: "1px solid", borderColor: "text.disabled" }} />
                                        <Typography variant="caption" color="text.secondary">Non-adaptive</Typography>
                                    </Box>
                                </Box>
                                {filteredGroups.map((g) => (
                                    <Box key={g.key} sx={{ mb: 1.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                            {g.label}
                                        </Typography>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                            {g.exams.map((e) => {
                                                const added = addedIds.has(e.id);
                                                const isSelected = selected?.id === e.id;
                                                const tip = `${e.title}${e.adaptive === false ? " · Non-adaptive" : e.adaptive ? " · Adaptive" : ""}${added ? " · already added" : ""}`;
                                                return (
                                                    <Tooltip key={e.id} title={tip} arrow>
                                                        <span>
                                                            <Chip
                                                                label={chipLabel(g.key, e)}
                                                                size="small"
                                                                icon={added ? <CheckIcon /> : undefined}
                                                                color={isSelected ? "primary" : added ? "default" : e.adaptive ? "info" : "default"}
                                                                variant={isSelected || added ? "filled" : "outlined"}
                                                                disabled={added}
                                                                onClick={() => setSelected({ id: e.id, title: e.title, adaptive: e.adaptive })}
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Field>

                    {selected && (
                        <Box sx={{ mt: 2, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                                Adding: <strong>{selected.title}</strong>
                                {selected.adaptive != null && ` · ${selected.adaptive ? "Adaptive" : "Non-adaptive"}`}
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <Field label="Schedule (ET — America/New_York)">
                                    <Box display="flex" gap="10px" flexWrap="wrap">
                                        <TextField
                                            label="Release" type="datetime-local" size="small" sx={{ minWidth: 210 }}
                                            InputLabelProps={{ shrink: true }}
                                            value={form.release_date}
                                            onChange={(e) => setForm({ ...form, release_date: e.target.value })}
                                        />
                                        <TextField
                                            label="Due" type="datetime-local" size="small" sx={{ minWidth: 210 }}
                                            InputLabelProps={{ shrink: true }}
                                            value={form.due_date}
                                            onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                                        />
                                        <TextField
                                            label="Order" type="number" size="small" sx={{ width: 90 }}
                                            value={form.order}
                                            onChange={(e) => setForm({ ...form, order: e.target.value })}
                                        />
                                    </Box>
                                </Field>
                                <Field label="Assign to">
                                    <AssignToField
                                        programUuid={programUuid}
                                        rosterParams={{ role: "PRACTICE" }}
                                        value={{ assign_mode: form.assign_mode, student_ids: form.student_ids }}
                                        onChange={(v) => setForm({ ...form, ...v })}
                                    />
                                </Field>
                                <Box display="flex" gap="10px">
                                    <Button variant="contained" size="small" onClick={handleAdd}>Add to program</Button>
                                    <Button size="small" onClick={() => setSelected(null)}>Cancel</Button>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddOpen(false)}>Done</Button>
                </DialogActions>
            </Dialog>

            {/* ---- Edit exam dialog (schedule + assignment) ---- */}
            <Dialog open={!!editExam} onClose={() => setEditExam(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{editExam ? `Edit ${editExam.exam_title}` : "Edit exam"}</DialogTitle>
                <DialogContent dividers>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Field label="Schedule (ET — America/New_York)">
                            <Box display="flex" gap="10px" flexWrap="wrap">
                                <TextField label="Release" type="datetime-local" size="small" sx={{ minWidth: 210 }}
                                    InputLabelProps={{ shrink: true }}
                                    value={editForm.release_date}
                                    onChange={(e) => setEditForm({ ...editForm, release_date: e.target.value })} />
                                <TextField label="Due" type="datetime-local" size="small" sx={{ minWidth: 210 }}
                                    InputLabelProps={{ shrink: true }}
                                    value={editForm.due_date}
                                    onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} />
                                <TextField label="Order" type="number" size="small" sx={{ width: 90 }}
                                    value={editForm.order}
                                    onChange={(e) => setEditForm({ ...editForm, order: e.target.value })} />
                            </Box>
                        </Field>
                        <Field label="Assign to">
                            <AssignToField
                                programUuid={programUuid}
                                rosterParams={{ role: "PRACTICE" }}
                                value={{ assign_mode: editForm.assign_mode, student_ids: editForm.student_ids }}
                                onChange={(v) => setEditForm({ ...editForm, ...v })}
                            />
                        </Field>
                        <TextField label="Description" size="small" fullWidth multiline
                            value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditExam(null)}>Cancel</Button>
                    <Button variant="contained" onClick={saveEditExam}
                        disabled={editForm.assign_mode === "SELECTED" && (editForm.student_ids?.length || 0) === 0}>
                        Save changes
                    </Button>
                </DialogActions>
            </Dialog>

            <ScheduleExamsDialog
                open={scheduleOpen}
                onClose={() => setScheduleOpen(false)}
                programUuid={programUuid}
                programType={programType}
                existingExams={exams}
                onCreated={() => { fetchExams(); if (onChange) onChange(); }}
            />

            <ConfirmDialog ref={confirmRef} />
        </Box>
    );
};

export default ProgramExamsPanel;
